// reports/EscalatedComplaintsReport.jsx
//
// "Escalated Complaints List" — a grouped, officer-wise listing of every
// complaint currently in the Escalated status.
//
// Visual language is deliberately identical to SingleComplaintReport.jsx:
// the same SUCCESS green palette, the same fixed F&W logo header with the
// generated-date block on the right, the same 1px black rule + page-number
// footer, and the same A4 page padding. Anything that changes there should
// change here too.

import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import './pdfFonts';
import { formatDcfText } from '../utils/displayText';
import { LEVEL_CODE, levelFromReference } from '../utils/escalation';

// Shared palette — kept byte-identical to SingleComplaintReport.jsx.
const SUCCESS = {
  50: '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
  900: '#14532d',
};

// Escalated is a red-flag status; the badge borrows the exact swatch used
// for `escalated` in PerformanceReport.jsx so the two reports agree.
const DANGER = {
  bg: '#fecaca',
  text: '#991b1b',
};

// Column widths as percentages. They must total 100.
// `pending` is deliberately generous: the "No. of Pending Complaints" header
// is long, and at anything under ~16% it hyphenates into three ragged lines.
const COL = {
  srNo: '7%',
  complaints: '25%',
  status: '13%',
  assigned: '38%',
  pending: '17%',
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'NotoNaskhArabic',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 30,
    paddingBottom: 50,
    paddingLeft: 20,
    paddingRight: 20,
  },
  // Reserved header height so flowed table rows never collide with the
  // fixed header block when a page break lands mid-table.
  headerBlock: {
    minHeight: 90,
  },
  dividerTop: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    marginVertical: 10,
  },
  heading: {
    fontSize: 20,
    marginBottom: 0,
    marginTop: 10,
    textAlign: 'center',
    fontWeight: 'bold',
    color: SUCCESS[800],
  },
  subHeading: {
    fontSize: 11,
    textAlign: 'center',
    marginTop: 4,
    marginBottom: 12,
    color: '#555',
    fontWeight: 'bold',
  },

  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: SUCCESS[200],
    borderRadius: 8,
    marginTop: 6,
    marginBottom: 10,
  },
  // `fixed` repeats this row at the top of every page so a long list stays
  // readable after a break.
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: SUCCESS[100],
    borderBottomWidth: 1,
    borderBottomColor: SUCCESS[200],
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: SUCCESS[100],
  },
  // Subtle zebra striping to keep long complaint-number stacks trackable
  // across the row.
  tableRowAlt: {
    backgroundColor: SUCCESS[50],
  },
  cellHeader: {
    paddingVertical: 7,
    paddingHorizontal: 6,
    fontSize: 9,
    fontWeight: 'bold',
    color: SUCCESS[800],
    borderRightWidth: 1,
    borderRightColor: SUCCESS[200],
  },
  cell: {
    paddingVertical: 6,
    paddingHorizontal: 6,
    fontSize: 9,
    color: '#333',
    borderRightWidth: 1,
    borderRightColor: SUCCESS[100],
  },
  cellLast: {
    borderRightWidth: 0,
  },
  srNoText: {
    fontSize: 9,
    color: '#333',
    textAlign: 'center',
  },
  complaintNumber: {
    fontSize: 9,
    color: '#333',
  },
  complaintEntry: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 2,
  },
  complaintDate: {
    fontSize: 8,
    color: '#666',
    marginLeft: 6,
  },
  assignedOfficer: {
    fontSize: 9,
    color: '#333',
    fontWeight: 'bold',
    marginBottom: 5,
  },
  assignedSubTable: {
    width: '100%',
    borderWidth: 1,
    borderColor: SUCCESS[200],
  },
  assignedSubHeader: {
    flexDirection: 'row',
    backgroundColor: SUCCESS[100],
    borderBottomWidth: 1,
    borderBottomColor: SUCCESS[200],
  },
  assignedSubRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: SUCCESS[100],
  },
  assignedSubRowLast: {
    borderBottomWidth: 0,
  },
  assignedSubHeaderCell: {
    paddingVertical: 3,
    paddingHorizontal: 2,
    fontSize: 6,
    fontWeight: 'bold',
    color: SUCCESS[800],
    borderRightWidth: 1,
    borderRightColor: SUCCESS[200],
  },
  assignedSubCell: {
    paddingVertical: 3,
    paddingHorizontal: 2,
    fontSize: 6,
    color: '#333',
    borderRightWidth: 1,
    borderRightColor: SUCCESS[100],
  },
  assignedSubCellLast: {
    borderRightWidth: 0,
  },
  unassignedText: {
    fontSize: 9,
    color: '#999',
    fontStyle: 'italic',
  },
  pendingText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: SUCCESS[900],
    textAlign: 'center',
  },
  statusBadge: {
    backgroundColor: DANGER.bg,
    color: DANGER.text,
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 4,
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },

  totalRow: {
    flexDirection: 'row',
    backgroundColor: SUCCESS[100],
    borderTopWidth: 1,
    borderTopColor: SUCCESS[200],
  },
  totalLabel: {
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontSize: 10,
    fontWeight: 'bold',
    color: SUCCESS[800],
    textAlign: 'right',
  },
  totalValue: {
    paddingVertical: 8,
    paddingHorizontal: 6,
    fontSize: 10,
    fontWeight: 'bold',
    color: SUCCESS[900],
    textAlign: 'center',
  },

  emptyState: {
    marginTop: 40,
    textAlign: 'center',
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
});

// dd-mm-yyyy, matching the "Escalated Complaints List 09-07-2026" title format.
const formatTitleDate = (date) => {
  const d = date ? new Date(date) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};

const formatGeneratedAt = (date) =>
  (date ? new Date(date) : new Date()).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

// Complaint dates are shown without a time to keep every number/date pair
// readable in the compact grouped cell. Use Pakistan time to match the
// timezone in which complaint timestamps are recorded by the backend.
const formatComplaintDate = (date) => {
  if (!date) return 'Date unavailable';

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Date unavailable';

  return parsed.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Asia/Karachi',
  });
};

const formatComplaintDateTime = (date) => {
  if (!date) return 'N/A';

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'N/A';

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Karachi',
  });
};

const detailValue = (value) => value || 'N/A';

// Derive the escalation level for a report row from its reference time (with a
// fallback to a stored level if an older payload still carries one).
const levelForRow = (c) =>
  levelFromReference('Escalated', c.escalation_reference) || c.escalation_level || null;

const getComplainantName = (complaint) =>
  detailValue(complaint.complainant_name || complaint.name);

const getNatureOfComplaint = (complaint) =>
  detailValue(
    complaint.nature_of_complaint ||
      complaint.complaint_nature ||
      complaint.complaint_nature_id?.name ||
      complaint.nature
  );

const EscalatedComplaintsReport = ({
  reportData = [],
  generatedAt,
  totalComplaints = 0,
  totalOfficers = 0,
  qrCodeUrl,
}) => {
  const rows = Array.isArray(reportData) ? reportData : [];

  // Trust the server's total when present, otherwise recompute so the
  // footer total can never disagree with the rows actually rendered.
  const computedTotal =
    totalComplaints ||
    rows.reduce((sum, group) => sum + (group.pending_count || 0), 0);

  return (
    <Document
      title={`Escalated Complaints List ${formatTitleDate(generatedAt)}`}
      author="Forest & Wildlife Department, Punjab"
      subject="Escalated Complaints List"
    >
      <Page size="A4" style={styles.page} wrap>
        {/* Fixed header — repeats on every page */}
        <View style={styles.headerBlock} fixed>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 0,
            }}
          >
            <Image
              src={process.env.PUBLIC_URL + '/assets/images/logo_fw.png'}
              style={{ width: 130, height: 'auto' }}
            />
            {/* Top-right block: QR to the live report, then the generated
                timestamp — same arrangement as SingleComplaintReport.jsx. */}
            <View style={{ flex: 0.5, alignItems: 'flex-end', justifyContent: 'flex-start' }}>
              {qrCodeUrl && <Image src={qrCodeUrl} style={{ width: 60, height: 60 }} />}
              <Text style={{ fontSize: 8, fontWeight: 'bold', marginTop: 2 }}>
                Escalated Complaints List
              </Text>
              <Text
                style={{
                  fontSize: 8,
                  textAlign: 'right',
                  marginBottom: 0,
                  marginTop: 5,
                  fontWeight: 'bold',
                }}
              >
                Generated Date: {formatGeneratedAt(generatedAt)}
              </Text>
            </View>
          </View>
          <View style={styles.dividerTop} />
        </View>

        {/* Title */}
        <Text style={styles.heading}>
          Escalated Complaints List {formatTitleDate(generatedAt)}
        </Text>
        <Text style={styles.subHeading}>
          {computedTotal} escalated complaint{computedTotal === 1 ? '' : 's'} across{' '}
          {totalOfficers || rows.filter((r) => r.assigned_to).length} officer
          {(totalOfficers || rows.filter((r) => r.assigned_to).length) === 1 ? '' : 's'}
        </Text>

        {rows.length === 0 ? (
          <Text style={styles.emptyState}>
            No escalated complaints found at the time this report was generated.
          </Text>
        ) : (
          <View style={styles.table}>
            {/* Header row repeats after every page break */}
            <View style={styles.tableHeader} fixed>
              <Text style={[styles.cellHeader, { width: COL.srNo, textAlign: 'center' }]}>
                Sr. No.
              </Text>
              <Text style={[styles.cellHeader, { width: COL.complaints }]}>Complaint No. / Date</Text>
              <Text style={[styles.cellHeader, { width: COL.status }]}>Status</Text>
              <Text style={[styles.cellHeader, { width: COL.assigned }]}>Assigned</Text>
              <Text
                style={[
                  styles.cellHeader,
                  styles.cellLast,
                  { width: COL.pending, textAlign: 'center' },
                ]}
              >
                No. of Pending Complaints
              </Text>
            </View>

            {rows.map((group, index) => {
              const numbers = Array.isArray(group.complaint_numbers)
                ? group.complaint_numbers
                : [];
              const complaintDetails = Array.isArray(group.complaint_details)
                ? group.complaint_details
                : numbers.map((complaintNumber) => ({
                    complaint_number: complaintNumber,
                    complaint_date: null,
                  }));

              // An officer with many complaints produces a tall cell. We do
              // NOT set wrap={false} on the row: a group with 30+ complaint
              // numbers can legitimately exceed a page, and forcing it to
              // stay whole would push it off the page entirely. react-pdf
              // splits it across pages and the fixed header row above keeps
              // the columns labelled.
              const assignedLabel = group.assigned_to?.designation || 'Unassigned';

              const isUnassigned = !group.assigned_to;

              return (
                <View
                  key={`${group.sr_no}-${index}`}
                  style={[styles.tableRow, index % 2 === 1 ? styles.tableRowAlt : null]}
                >
                  <View style={[styles.cell, { width: COL.srNo }]}>
                    <Text style={styles.srNoText}>{group.sr_no ?? index + 1}</Text>
                  </View>

                  <View style={[styles.cell, { width: COL.complaints }]}>
                    {complaintDetails.length > 0 ? (
                      complaintDetails.map((complaint, i) => (
                        <View
                          key={`${complaint.complaint_number}-${i}`}
                          style={styles.complaintEntry}
                        >
                          <Text style={styles.complaintNumber}>
                            {complaint.complaint_number}
                          </Text>
                          <Text style={styles.complaintDate}>
                            {formatComplaintDate(complaint.complaint_date)}
                          </Text>
                        </View>
                      ))
                    ) : (
                      <Text style={styles.unassignedText}>—</Text>
                    )}
                  </View>

                  <View style={[styles.cell, { width: COL.status }]}>
                    <Text style={styles.statusBadge}>{group.status || 'Escalated'}</Text>
                  </View>

                  <View style={[styles.cell, { width: COL.assigned }]}>
                    <Text style={isUnassigned ? styles.unassignedText : styles.assignedOfficer}>
                      {formatDcfText(assignedLabel)}
                    </Text>
                    {complaintDetails.length > 0 && (
                      <View style={styles.assignedSubTable}>
                        <View style={styles.assignedSubHeader}>
                          <Text style={[styles.assignedSubHeaderCell, { width: '26%' }]}>
                            Complainant
                          </Text>
                          <Text style={[styles.assignedSubHeaderCell, { width: '25%' }]}>
                            Nature
                          </Text>
                          <Text style={[styles.assignedSubHeaderCell, { width: '25%' }]}>
                            Date Time
                          </Text>
                          <Text
                            style={[
                              styles.assignedSubHeaderCell,
                              styles.assignedSubCellLast,
                              { width: '24%' },
                            ]}
                          >
                            Complaint No.
                          </Text>
                        </View>
                        {complaintDetails.map((complaint, i) => (
                          <View
                            key={`assigned-detail-${complaint.complaint_number}-${i}`}
                            style={[
                              styles.assignedSubRow,
                              i === complaintDetails.length - 1
                                ? styles.assignedSubRowLast
                                : null,
                            ]}
                          >
                            <Text style={[styles.assignedSubCell, { width: '26%' }]}>
                              {getComplainantName(complaint)}
                            </Text>
                            <Text style={[styles.assignedSubCell, { width: '25%' }]}>
                              {getNatureOfComplaint(complaint)}
                            </Text>
                            <Text style={[styles.assignedSubCell, { width: '25%' }]}>
                              {formatComplaintDateTime(complaint.complaint_date)}
                            </Text>
                            <Text
                              style={[
                                styles.assignedSubCell,
                                styles.assignedSubCellLast,
                                { width: '24%' },
                              ]}
                            >
                              {detailValue(complaint.complaint_number)}
                              {(() => {
                                const lvl = levelForRow(complaint);
                                return lvl ? ` (${LEVEL_CODE[lvl] || lvl})` : '';
                              })()}
                            </Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </View>

                  <View style={[styles.cell, styles.cellLast, { width: COL.pending }]}>
                    <Text style={styles.pendingText}>{group.pending_count ?? numbers.length}</Text>
                  </View>
                </View>
              );
            })}

            {/* Grand total */}
            <View style={styles.totalRow} wrap={false}>
              <Text
                style={[
                  styles.totalLabel,
                  {
                    width: `${
                      parseFloat(COL.srNo) +
                      parseFloat(COL.complaints) +
                      parseFloat(COL.status) +
                      parseFloat(COL.assigned)
                    }%`,
                  },
                ]}
              >
                Total Escalated Complaints:
              </Text>
              <Text style={[styles.totalValue, { width: COL.pending }]}>{computedTotal}</Text>
            </View>
          </View>
        )}

        {/* Fixed footer — identical to SingleComplaintReport.jsx */}
        <View
          style={{
            position: 'absolute',
            bottom: 10,
            left: 20,
            right: 20,
            flexDirection: 'column',
            alignItems: 'center',
          }}
          fixed
        >
          <View
            style={{
              width: '100%',
              height: 1,
              backgroundColor: 'black',
              marginBottom: 5,
              marginTop: 10,
            }}
          />
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
              fontSize: 6,
            }}
          >
            <Text>F&W Department, Punjab, Pakistan</Text>
            <Text
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default EscalatedComplaintsReport;
